
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/solid";

export function VetComparisonSection() {
  const { t } = useTranslation();

  const withoutFeatures = [
    t("comparison.manualPaperwork", "Hours of manual paperwork"),
    t("comparison.lessPatientTime", "Less time with patients"),
    t("comparison.stressedWork", "Stressed & Overwhelmed"),
    t("comparison.lostAnnually", "520h lost annually"),
  ];

  const withFeatures = [
    t("comparison.aiPowered", "AI-powered SOAP notes"),
    t("comparison.morePatientCare", "More patient care"),
    t("comparison.automated", "Automated & Efficient"),
    t("comparison.timeSavedAnnually", "520h saved annually"),
  ];

  return (
    <section className="py-24 relative !overflow-visible">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#00342d] mb-6 tracking-tight"
            style={{ fontFamily: "'Bruno Ace', sans-serif" }}
          >
            {t("comparison.title", "Transform Your Veterinary Practice")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 leading-relaxed"
          >
            {t(
              "comparison.subtitle",
              "See the difference VettConsult makes in your daily practice"
            )}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto !overflow-visible p-4">
          {/* Without VettConsult */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-8 border border-gray-200 shadow-sm relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-2 bg-gray-200" />
            <h3 className="text-2xl font-bold text-gray-500 mb-8 text-center">
              {t("comparison.without", "Without VettConsult")}
            </h3>
            <div className="space-y-6">
              {withoutFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <XMarkIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-lg text-gray-500 font-medium">{feature}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* With VettConsult */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-8 border border-[#00e9be]/30 shadow-xl relative overflow-hidden transform md:-translate-y-4"
          >
            <div className="absolute top-0 left-0 w-full h-2 bg-[#00e9be]" />
            <h3 className="text-2xl font-bold text-[#00342d] mb-8 text-center">
              {t("comparison.with", "With VettConsult")}
            </h3>
            <div className="space-y-6">
              {withFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00e9be]/10 flex items-center justify-center flex-shrink-0">
                    <CheckIcon className="w-5 h-5 text-[#00e9be]" />
                  </div>
                  <p className="text-lg text-[#00342d] font-bold">{feature}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
